import { config, useUserConfig } from '@littlebird/sdk';
import { useRouter } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { Fragment, useMemo, useState } from 'react';
import { singularSdk } from 'singular-sdk';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { BaaFeatureLimits } from '@/components/blocks/baa-feature-limits';
import { BaaAgreementModal } from '@/components/modals/BaaAgreementModal';
import { DeleteAccountModal } from '@/components/modals/DeleteAccountModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { getEnvVar, isProd } from '@/env';
import { ContextDeleteModal } from '@/features/context-collection/context-delete-modal';
import { electronApis } from '@/features/electron-api';
import { teamStore } from '@/features/team/team.store';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';
import { inElectron } from '@/utils/capabilities';

import { SettingsSection } from '../settings-section';

export const DataAndPrivacySettings = observer(() => {
  const canAccessResetOnboarding = userStore.isInternalUser;
  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Data Controls</SettingsSection.Title>
      </SettingsSection.Header>

      <SettingsSection.Group title="Manage Context Collected">
        {userStore.backendChoice === 'typescript' && (
          <>
            <ScreenshotPreviewsItem />
            <SettingsSection.Seperator />
          </>
        )}
        <DataDeletionSection />
        <SettingsSection.Seperator />
        <DeleteCustomRangeItem />
        <SettingsSection.Seperator />
        <DeleteAllContextItem />
      </SettingsSection.Group>
      <SettingsSection.Group title="Compliance">
        <BaaAgreementItem />
      </SettingsSection.Group>
      <SettingsSection.Group title="Manage your Account">
        <DeleteAccountItem />
        {canAccessResetOnboarding && (
          <>
            <SettingsSection.Seperator />
            <ResetOnboarding />
            {!isProd && (
              <>
                <SettingsSection.Seperator />
                <SingularDeviceId />
              </>
            )}
          </>
        )}
      </SettingsSection.Group>
    </SettingsSection.Root>
  );
});

const ScreenshotPreviewsItem = observer(() => {
  const userConfig = useUserConfig();
  return (
    <SettingsSection.Item searchId="data-controls.screenshot-previews">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel id="data-controls-screenshot-previews">
          Keep Previews of Phone Screenshots
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Only you can see them.
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        id="data-controls-screenshot-previews"
        checked={config.privacy.save_screenshot_previews}
        onCheckedChange={(checked) =>
          userConfig.privacy.set('save_screenshot_previews', checked)
        }
      />
    </SettingsSection.Item>
  );
});

const BaaAgreementItem = observer(() => {
  const flags = userStore.user?.feature_flags;
  const team = teamStore.dashboard?.team;
  const hasSignedBaa = Boolean(flags?.has_signed_baa);
  const hipaaEnabled = hasSignedBaa || Boolean(team?.baa_enabled);
  const signedAt = flags?.baa_signed_at as string | undefined;
  const signedName = flags?.baa_signed_name as string | undefined;

  let description: string;
  let action: React.ReactNode = null;

  if (team && (team.baa_enabled || !hasSignedBaa)) {
    description = team.baa_enabled
      ? 'HIPAA is enforced for your account by your team.'
      : 'HIPAA compliance is managed by your team.';
    if (team.baa_enabled) {
      action = (
        <Button
          variant="secondary"
          title="View agreement"
          onClick={() => {
            window.open(
              `${getEnvVar('VITE_API_URL')}/static/littlebird-baa.pdf`,
              '_blank',
              'noopener,noreferrer'
            );
          }}
          className={cn('w-36')}
        >
          View agreement
        </Button>
      );
    }
  } else if (hasSignedBaa) {
    const when = signedAt
      ? new Date(signedAt).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : null;
    description = signedName
      ? `Signed by ${signedName}${when ? ` on ${when}` : ''}. Privacy mode is enforced for your account.`
      : 'Signed. Privacy mode is enforced for your account.';
    action = (
      <Button
        variant="secondary"
        title="View agreement"
        onClick={() => {
          window.open(
            `${getEnvVar('VITE_API_URL')}/static/littlebird-baa.pdf`,
            '_blank',
            'noopener,noreferrer'
          );
        }}
        className={cn('w-36')}
      >
        View agreement
      </Button>
    );
  } else {
    description =
      'Sign a HIPAA Business Associate Agreement (BAA) if you need HIPAA privacy mode. Enabling it limits some features.';
    action = (
      <Button
        variant="secondary"
        title="View and accept"
        onClick={() => {
          ModalsManager.show(BaaAgreementModal, {
            resolve: () => {},
          });
        }}
        className={cn('w-36')}
      >
        View and accept
      </Button>
    );
  }

  return (
    <SettingsSection.Item direction="horizontal" searchId="data-controls.hipaa">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          {hipaaEnabled ? 'HIPAA privacy mode is on' : 'Enable HIPAA'}
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          {description}
        </SettingsSection.ItemDescription>
        {hipaaEnabled && (
          <div className="space-y-2 text-xs text-foreground-muted">
            <BaaFeatureLimits />
            <p>You cannot turn this mode off in Settings.</p>
          </div>
        )}
      </SettingsSection.ItemHeader>
      {action}
    </SettingsSection.Item>
  );
});

const DeleteAllContextItem = () => {
  return (
    <DeletionItem
      title="Delete All Context"
      description="Permanently remove all context collected. This action cannot be undone."
      buttonTitle="Delete"
      searchId="data-controls.delete-all-context"
      onClick={async () => {
        await backendApis.inputs.deleteInputs(0);
        await electronApis.context.deleteContext();
      }}
    />
  );
};

const DataDeletionSection = () => {
  const settings: Array<{
    title: string;
    description: string;
    buttonTitle: string;
    searchId: string;
    onClick: () => Promise<void>;
  }> = useMemo(() => {
    return [
      {
        title: 'Delete Last Hour of Context',
        description: 'Remove all context collected in the last hour',
        buttonTitle: 'Delete',
        searchId: 'data-controls.delete-last-hour',
        onClick: async () => {
          const time = new Date(Date.now() - 60 * 60 * 1000).getTime();
          await backendApis.inputs.deleteInputs(time);
          await electronApis.context.deleteContext(time);
        },
      },
      {
        title: 'Delete Last Day of Context',
        description: 'Remove all context collected in the last 24 hours',
        buttonTitle: 'Delete',
        searchId: 'data-controls.delete-last-day',
        onClick: async () => {
          const time = new Date(Date.now() - 24 * 60 * 60 * 1000).getTime();
          await backendApis.inputs.deleteInputs(time);
          await electronApis.context.deleteContext(time);
        },
      },
    ];
  }, []);

  return (
    <>
      {settings.map((setting, index) => (
        <Fragment key={setting.title}>
          <DeletionItem
            title={setting.title}
            description={setting.description}
            buttonTitle={setting.buttonTitle}
            searchId={setting.searchId}
            onClick={setting.onClick}
          />
          {index !== settings.length - 1 && <SettingsSection.Seperator />}
        </Fragment>
      ))}
    </>
  );
};

const DeleteCustomRangeItem = () => {
  return (
    <SettingsSection.Item
      direction="horizontal"
      searchId="data-controls.delete-custom-range"
    >
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Delete Context for a Time Period
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Remove a recent stretch or specific dates, e.g. all of March
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Button
        variant={'secondary'}
        title={'Select'}
        onClick={() => {
          ModalsManager.show(ContextDeleteModal, {
            resolve: () => {},
          });
        }}
        className={cn('w-24')}
      >
        Select
      </Button>
    </SettingsSection.Item>
  );
};

const DeleteAccountItem = () => {
  return (
    <SettingsSection.Item
      direction="horizontal"
      searchId="data-controls.delete-account"
    >
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Delete Your Account
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Permanently delete your account and all associated data
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Button
        variant={'secondary'}
        title={'Delete'}
        onClick={() => {
          ModalsManager.show(DeleteAccountModal, {
            resolve: () => {},
          });
        }}
        className={cn('w-24')}
      >
        Delete
      </Button>
    </SettingsSection.Item>
  );
};

interface IDeletionItem {
  title: string;
  description: string;
  buttonTitle: string;
  searchId?: string;
  onClick: () => Promise<void>;
}
const DeletionItem = ({
  title,
  description,
  buttonTitle,
  searchId,
  onClick,
}: IDeletionItem) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleOnClick = async () => {
    if (showConfirmation) {
      try {
        setIsProcessing(true);
        await onClick();
        setShowConfirmation(false);
      } catch (err) {
        console.error(`Error - ${title} : `, err);
      } finally {
        setIsProcessing(false);
      }
    } else {
      setShowConfirmation(true);
    }
  };

  return (
    <SettingsSection.Item direction="horizontal" searchId={searchId}>
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>{title}</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          {description}
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Button
        variant={'secondary'}
        title={buttonTitle}
        onClick={handleOnClick}
        className={cn(
          'w-24',
          showConfirmation && 'text-danger-9 hover:text-danger-10'
        )}
        isLoading={isProcessing}
        disabled={isProcessing}
      >
        {showConfirmation ? 'Confirm?' : buttonTitle}
      </Button>
    </SettingsSection.Item>
  );
};

const SingularDeviceId = () => {
  const [copied, setCopied] = useState(false);

  const deviceId = (() => {
    try {
      return singularSdk.getSingularDeviceId();
    } catch {
      return 'unavailable';
    }
  })();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(deviceId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <SettingsSection.Item
      direction="horizontal"
      searchId="data-controls.singular-device-id"
    >
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Singular Device ID
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          {deviceId}
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Button variant="secondary" className="w-24" onClick={handleCopy}>
        {copied ? 'Copied!' : 'Copy ID'}
      </Button>
    </SettingsSection.Item>
  );
};

const ResetOnboarding = () => {
  const router = useRouter();
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const handleClick = async () => {
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }
    try {
      if (inElectron()) {
        await electronApis.context.resetContextCollectionConsent();
      }
      await userStore.syncUpdate(
        { name: '', is_onboarded_desktop: false },
        { throwOnError: true }
      );
    } catch (error) {
      console.error('Failed to reset onboarding:', error);
      toast.error('Could not reset onboarding. Please try again.');
      return;
    }
    setShowConfirmation(false);
    router.invalidate();
  };

  return (
    <SettingsSection.Item searchId="data-controls.reset-onboarding">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Reset Onboarding</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Clear onboarding progress to test new onboarding experiences
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Button
        variant={'secondary'}
        title={'Reset'}
        onClick={handleClick}
        className={cn('w-24')}
      >
        {showConfirmation ? 'Confirm?' : 'Reset'}
      </Button>
    </SettingsSection.Item>
  );
};
